import { SEO } from "@/components/SEO";
import { motion, useInView } from "framer-motion";
import { Quote, TrendingUp, Clock, Users, Target, AlertCircle, CheckCircle, Building2, Award, Zap, HeartHandshake } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import thaiCeoOffice from "@/assets/thai-ceo-office.jpg";
import globalTradeTeam from "@/assets/global-trade-team.jpg";
import pacificPartnership from "@/assets/pacific-partnership.jpg";

// Animated counter component
function AnimatedCounter({ 
  value, 
  suffix = "",
  prefix = ""
}: { 
  value: number; 
  suffix?: string;
  prefix?: string;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!isInView) return;
    
    const duration = 2000;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{displayValue}{suffix}
    </span>
  );
}

// Parse metric value
function parseMetricValue(value: string): { number: number; prefix: string; suffix: string } {
  // Handle special cases like "24/7"
  if (value === "24/7") {
    return { number: 24, prefix: "", suffix: "/7" };
  }
  
  const match = value.match(/([^\d]*)(\d+)([^\d]*)/);
  if (match) {
    return {
      prefix: match[1] || "",
      number: parseInt(match[2] ?? "0"),
      suffix: match[3] || ""
    };
  }
  return { number: 0, prefix: "", suffix: value };
}

export default function Customers() {
  const { t } = useLanguage();

  const metrics = [
    { value: "100+", label: t("customers.metric1"), icon: Building2, color: "from-emerald-500 to-teal-500" },
    { value: "3", label: t("customers.metric2"), icon: Zap, color: "from-blue-500 to-cyan-500" },
    { value: "300%", label: t("customers.metric3"), icon: TrendingUp, color: "from-orange-500 to-amber-500" },
    { value: "24/7", label: t("customers.metric4"), icon: HeartHandshake, color: "from-purple-500 to-pink-500" },
  ];

  const caseStudies = [
    {
      type: t("customers.case1.type"),
      company: t("customers.case1.company"),
      challenge: t("customers.case1.challenge"),
      solution: t("customers.case1.solution"),
      results: [
        { value: "↑ 300%", label: t("customers.case1.result1") },
        { value: "3", label: t("customers.case1.result2") },
        { value: "↓ 50%", label: t("customers.case1.result3") },
      ],
      quote: t("customers.case1.quote"),
      image: "case1",
    },
    {
      type: t("customers.case3.type"),
      company: t("customers.case3.company"),
      challenge: t("customers.case3.challenge"),
      solution: t("customers.case3.solution"),
      results: [
        { value: "↑ 250%", label: t("customers.case3.result1") },
        { value: "↑ 13%", label: t("customers.case3.result2") },
        { value: "3", label: t("customers.case3.result3") },
      ],
      quote: t("customers.case3.quote"),
      image: "case3",
    },
    {
      type: t("customers.case2.type"),
      company: t("customers.case2.company"),
      challenge: t("customers.case2.challenge"),
      solution: t("customers.case2.solution"),
      results: [
        { value: "2-3x", label: t("customers.case2.result1") },
        { value: "< 20%", label: t("customers.case2.result2") },
        { value: "↓ 85%", label: t("customers.case2.result3") },
      ],
      quote: t("customers.case2.quote"),
      image: "case2",
    },
  ];

  const testimonials = [
    {
      quote: t("customers.testimonial1.quote"),
      author: t("customers.testimonial1.author"),
      company: t("customers.testimonial1.company"),
    },
    {
      quote: t("customers.testimonial2.quote"),
      author: t("customers.testimonial2.author"),
      company: t("customers.testimonial2.company"),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Freight Case Studies & Success | CargoNova"
        description="Real case studies from freight forwarders across SE Asia. See how CargoNova customers achieve 99.5% on-time delivery and 30% faster turnaround."
        canonical="https://cargonova.ai/customers"
        titleZh="货代客户案例与成果 | CargoNova"
        descriptionZh="东南亚货代真实案例，看 CargoNova 客户如何实现 99.5% 准时交付与 30% 作业提效。"
        canonicalZh="https://cargonova.ai/zh/customers"
        canonicalTh="https://cargonova.ai/th/customers"
      />
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section with Metrics */}
        <section className="relative pb-12 bg-gradient-to-b from-secondary/10 via-secondary/5 to-background overflow-hidden -mt-24 pt-24">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10 py-10">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                {t("customers.pageTitle")}
              </h1>
              <p className="text-xl text-muted-foreground">
                {t("customers.pageSubtitle")}
              </p>
            </motion.div>

            {/* Metrics Card - Compact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative bg-card/60 backdrop-blur-2xl rounded-2xl border border-transparent hover:border-secondary/50 shadow-[0_8px_32px_-8px_hsl(var(--secondary)/0.3)] overflow-hidden transition-all duration-300 group/card">
                {/* 4 Metrics in a unified row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 p-8">
                  {metrics.map((metric, index) => {
                    const parsed = parseMetricValue(metric.value);
                    const IconComponent = metric.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                        className="relative flex flex-col items-center text-center"
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center shadow-md mb-3 group-hover/card:scale-110 transition-transform duration-300`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        
                        {/* Number */}
                        <div className={`text-2xl md:text-3xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent mb-1`}>
                          <AnimatedCounter 
                            value={parsed.number} 
                            prefix={parsed.prefix}
                            suffix={parsed.suffix}
                          />
                        </div>
                        
                        {/* Label */}
                        <div className="text-sm md:text-base font-semibold text-foreground leading-tight">
                          {metric.label}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Case Studies Section */}
        <section className="pt-4 pb-10">
          <div className="container mx-auto px-4">
            <h2 className="sr-only">{t("customers.caseStudiesHeading")}</h2>
            <div className="space-y-12">
              {caseStudies.map((caseStudy, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative rounded-2xl border border-border hover:shadow-lg transition-all duration-300 overflow-hidden bg-card"
                >
                  {/* Background image for case studies */}
                  {caseStudy.image === "case1" && (
                    <>
                      <div className="absolute right-0 top-0 w-1/2 h-full">
                        <img 
                          src={thaiCeoOffice} 
                          alt="Southeast Asia freight company CEO" 
                          className="w-full h-full object-cover object-[center_35%]"
                        />
                      </div>
                      <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-r from-card via-card/10 to-transparent" />
                    </>
                  )}
                  {caseStudy.image === "case3" && (
                    <>
                      <div className="absolute right-0 top-0 w-1/2 h-full">
                        <img 
                          src={pacificPartnership} 
                          alt="Global partnership executives" 
                          className="w-full h-full object-cover object-[center_55%]"
                        />
                      </div>
                      <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-r from-card via-card/10 to-transparent" />
                    </>
                  )}
                  {caseStudy.image === "case2" && (
                    <>
                      <div className="absolute right-0 top-0 w-1/2 h-full">
                        <img 
                          src={globalTradeTeam} 
                          alt="Global trade team collaboration" 
                          className="w-full h-full object-cover object-[60%_40%]"
                        />
                      </div>
                      <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-r from-card via-card/10 to-transparent" />
                    </>
                  )}

                  <div className="relative z-10 p-8 md:p-12">
                    <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
                      {caseStudy.type}
                    </span>
                    <h3 className="text-2xl font-bold text-foreground mb-6">
                      {caseStudy.company}
                    </h3>
                    
                    <div className="space-y-4 mb-6 max-w-xl">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-amber-600 mb-1">
                            {t("customers.challengeLabel")}
                          </div>
                          <p className="text-muted-foreground">{caseStudy.challenge}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-secondary mb-1">
                            {t("customers.solutionLabel")}
                          </div>
                          <p className="text-muted-foreground">{caseStudy.solution}</p>
                        </div>
                      </div>
                    </div>

                    {/* Quote and Results in same row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                      <div className="bg-secondary/5 backdrop-blur-xs rounded-xl p-4 border-l-4 border-secondary">
                        <Quote className="w-5 h-5 text-secondary mb-2" />
                        <p className="text-foreground italic text-sm">{caseStudy.quote}</p>
                      </div>
                      
                      {/* Results Card - No title */}
                      <div className="bg-gradient-to-br from-secondary/10 to-accent/10 backdrop-blur-xs rounded-xl p-4">
                        <div className="grid grid-cols-3 gap-2">
                          {caseStudy.results.map((result, i) => {
                            const isIncrease = result.value.includes('↑') || result.value.includes('x');
                            const isDecrease = result.value.includes('↓') || result.value.includes('<');
                            const hasArrow = result.value.includes('↑') || result.value.includes('↓');
                            const displayValue = result.value.replace('↑', '').replace('↓', '').trim();
                            const arrow = result.value.includes('↑') ? '↑' : result.value.includes('↓') ? '↓' : '';
                            
                            return (
                              <div key={i} className="text-center">
                                <div className={`text-xl md:text-2xl font-extrabold mb-1 flex items-center justify-center gap-0.5 ${isDecrease ? 'text-secondary' : isIncrease ? 'text-orange-500' : 'text-foreground'}`}>
                                  <span>{displayValue}</span>
                                  {hasArrow && <span>{arrow}</span>}
                                </div>
                                <div className="text-xs text-muted-foreground">{result.label}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-center text-foreground mb-12"
            >
              {t("customers.testimonialsTitle")}
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-8 border border-border"
                >
                  <Quote className="w-8 h-8 text-secondary/30 mb-4" />
                  <p className="text-foreground mb-6 leading-relaxed">{testimonial.quote}</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-ai flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">{testimonial.author}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.company}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t("customers.ctaTitle")}
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {t("customers.ctaSubtitle")}
              </p>
              <a href="https://www.deep-insights.ai/about" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-gradient-ai hover:opacity-90 text-primary-foreground shadow-ai">
                  {t("nav.consult")}
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
